import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = null;

function LogoMark() {
  return (
    <div className="bg-[#1a1a1a] rounded-2xl px-5 py-3 inline-flex flex-col justify-center" style={{ minWidth: 160 }}>
      <div className="flex items-start">
        <span className="text-white text-[22px] font-extrabold leading-none tracking-tight" style={{ fontFamily: '"Outfit", sans-serif' }}>
          SalesCorp
        </span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="ml-0.5 -mt-0.5 flex-shrink-0">
          <path d="M7 17L17 7" stroke="#FF6600" strokeWidth="3" strokeLinecap="round"/>
          <path d="M7 7H17V17" stroke="#FF6600" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <span className="text-[#FF6600] text-[13px] font-medium tracking-[0.08em] leading-none mt-1" style={{ fontFamily: '"Outfit", sans-serif' }}>
        australia
      </span>
    </div>
  );
}

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Contact", to: "/contact" },
  { label: "FAQ", to: "/faq" },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-gray-200/50 shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <LogoMark />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 text-[15px] font-medium rounded-full transition-all duration-200 ${
                  location.pathname === link.to
                    ? "text-[#FF6600] bg-[#FF6600]/8"
                    : "text-[#2E2E33] hover:text-[#FF6600]"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:1300796797"
              className="flex items-center gap-2 text-[15px] font-semibold text-[#2E2E33] hover:text-[#FF6600] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              1300 796 797
            </a>
            <Link to="/compare" data-testid="header-compare-btn">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-6 py-3 text-[15px] shadow-lg shadow-[#FF6600]/20 hover:shadow-xl hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5">
                Compare Now
              </Button>
            </Link>
            <a
              href="https://nbe-signup.uconx.com.au/p/salescorp-australia"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-signup-btn"
            >
              <Button className="bg-[#1a1a1a] hover:bg-[#000] text-white font-bold rounded-full px-7 py-3 text-[15px] border-2 border-[#FF6600] shadow-lg shadow-[#FF6600]/25 hover:shadow-xl hover:shadow-[#FF6600]/40 transition-all hover:-translate-y-0.5 animate-pulse-subtle">
                Sign Up
              </Button>
            </a>
          </div>

          <button
            className="lg:hidden p-2 rounded-xl hover:bg-[#F8FAFC] transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6 text-[#2E2E33]" /> : <Menu className="w-6 h-6 text-[#2E2E33]" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-5 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${
                  location.pathname === link.to
                    ? "text-[#FF6600] bg-[#FF6600]/8"
                    : "text-[#2E2E33] hover:bg-[#F8FAFC]"
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
              <a href="tel:1300796797" className="flex items-center gap-2 px-4 py-2 text-[15px] font-semibold text-[#2E2E33]">
                <Phone className="w-4 h-4" /> 1300 796 797
              </a>
              <Link to="/compare" className="block px-4">
                <Button className="w-full bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full text-[15px] py-3" data-testid="mobile-compare-btn">
                  Compare Now
                </Button>
              </Link>
              <a
                href="https://nbe-signup.uconx.com.au/p/salescorp-australia"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4"
              >
                <Button className="w-full bg-[#1a1a1a] hover:bg-[#000] text-white font-bold rounded-full text-[15px] py-3 border-2 border-[#FF6600] shadow-lg shadow-[#FF6600]/25" data-testid="mobile-signup-btn">
                  Sign Up
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#2E2E33]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-5">
              <LogoMark />
            </div>
            <p className="text-gray-400 text-[15px] leading-relaxed mb-5">
              Helping Australians compare and save on electricity. Based in Sydney, servicing all of Australia.
            </p>
            <p className="text-gray-500 text-sm">Sydney, Australia</p>
          </div>

          <div>
            <h4 className="font-bold text-white text-lg mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-[#FF6600] text-[15px] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white text-lg mb-5">Services</h4>
            <ul className="space-y-3 text-[15px] text-gray-400">
              <li><Link to="/services" className="hover:text-[#FF6600] transition-colors">Electricity Comparison</Link></li>
              <li><Link to="/services" className="hover:text-[#FF6600] transition-colors">Business Electricity</Link></li>
              <li><Link to="/services" className="hover:text-[#FF6600] transition-colors">Business Energy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-white text-lg mb-5">Contact</h4>
            <ul className="space-y-4 text-[15px] text-gray-400">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#FF6600]" />
                <a href="tel:1300796797" className="hover:text-[#FF6600] transition-colors">1300 796 797</a>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5 text-[#FF6600] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                <a href="mailto:hello@salescorpaustralia.com.au" className="hover:text-[#FF6600] transition-colors break-all">hello@salescorpaustralia.com.au</a>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5 text-[#FF6600] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span>Sydney, NSW, Australia</span>
              </li>
            </ul>
            <div className="mt-8">
              <Link to="/compare">
                <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-6 py-3 text-[15px]" data-testid="footer-compare-btn">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Sales Corp Australia. All rights reserved.</p>
          <p className="text-gray-600 text-xs text-center md:text-right max-w-xl">
            Sales Corp Australia is a comparison service. We do not compare all brands or products in the market.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: '"Outfit", sans-serif' }}>
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
